import { useEffect, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  AdditionalCostsInput,
  RenovationCostsInput,
  RunningCostsInput,
  HoldingCostsInput,
} from '@/components/costs';
import { usePropertyStore } from '@/stores/propertyStore';
import {
  STATUS_CONFIG,
  INVESTMENT_TYPE_LABELS,
  PROPERTY_TYPE_LABELS,
  DISMISSAL_REASON_LABELS,
  DEAL_SOURCE_LABELS,
  eurosToCents,
  centsToEuros,
  cn,
} from '@/lib/utils';
import type { 
  Property, 
  RenovationCostItem,
  Bundesland
} from '@/types';

const propertySchema = z.object({
  name: z.string().min(1, 'Name ist erforderlich'),
  address_street: z.string().optional().nullable(),
  address_zip: z.string().optional().nullable(),
  address_city: z.string().optional().nullable(),
  bundesland: z.string().optional().nullable(),
  property_type: z.enum(['etw', 'mfh', 'efh', 'grundstueck', 'gewerbe', 'sonstige']),
  living_area_sqm: z.number().min(0).optional().nullable(),
  land_area_sqm: z.number().min(0).optional().nullable(),
  year_built: z.number().int().min(1000).max(2100).optional().nullable(),
  num_units: z.number().int().min(1).optional().nullable(),
  floor: z.number().int().optional().nullable(),
  num_floors: z.number().int().min(1).optional().nullable(),
  status: z.enum(['watchlist', 'in_review', 'offer_submitted', 'purchased', 'in_renovation', 'for_sale', 'sold', 'in_portfolio', 'dismissed']),
  investment_type: z.enum(['buy_and_hold', 'fix_and_flip', 'self_used', 'undecided']),
  dismissal_reason: z.enum(['too_expensive', 'renovation_too_complex', 'bad_location', 'financing_failed', 'seller_backed_out', 'legal_issues', 'other']).optional().nullable(),
  dismissal_notes: z.string().optional().nullable(),
  deal_source: z.enum(['immoscout', 'immowelt', 'ebay', 'makler', 'off_market', 'netzwerk', 'zwangsversteigerung', 'bank', 'sonstige']).optional().nullable(),
  source_contact_name: z.string().optional().nullable(),
  source_contact_phone: z.string().optional().nullable(),
  source_contact_email: z.string().optional().nullable(),
  
  // Kaufpreis
  purchase_price: z.number().min(0).optional().nullable(),

  // Kaufnebenkosten
  additional_costs: z.number().min(0).optional().nullable(),
  cost_transfer_tax: z.number().min(0).optional().nullable(),
  cost_notary: z.number().min(0).optional().nullable(),
  cost_land_registry: z.number().min(0).optional().nullable(),
  cost_broker: z.number().min(0).optional().nullable(),
  cost_other: z.number().min(0).optional().nullable(),

  // Sanierung
  renovation_cost_total: z.number().min(0).optional().nullable(),

  // Haltekosten
  holding_period_start: z.string().optional().nullable(),
  holding_period_end: z.string().optional().nullable(),
  holding_cost_monthly: z.number().min(0).optional().nullable(),

  // Laufende Kosten
  running_cost_hausgeld: z.number().min(0).optional().nullable(),
  running_cost_hausgeld_non_allocable: z.number().min(0).optional().nullable(),
  running_cost_reserve: z.number().min(0).optional().nullable(),
  running_cost_management: z.number().min(0).optional().nullable(),
  running_cost_insurance: z.number().min(0).optional().nullable(),
  running_cost_property_tax: z.number().min(0).optional().nullable(),
  running_cost_other: z.number().min(0).optional().nullable(),

  // Mieteinnahmen
  rent_cold: z.number().min(0).optional().nullable(),
  rent_warm: z.number().min(0).optional().nullable(),
  current_value: z.number().min(0).optional().nullable(),

  notes: z.string().optional().nullable(),
});

type FormData = z.infer<typeof propertySchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  property?: Property | null;
}

const FORM_TABS = [
  { id: 'basic', label: 'Basisdaten' },
  { id: 'costs', label: 'Kosten' },
  { id: 'income', label: 'Einnahmen' },
  { id: 'source', label: 'Quelle' },
  { id: 'notes', label: 'Notizen' },
];

export function PropertyFormModal({ open, onOpenChange, property }: Props) {
  const { createProperty, updateProperty, isLoading } = usePropertyStore();
  const [activeTab, setActiveTab] = useState('basic');
  const [renovationItems, setRenovationItems] = useState<RenovationCostItem[]>([]);
  const isEditing = !!property;

  const { register, handleSubmit, reset, control, setValue, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(propertySchema),
    defaultValues: {
      name: '',
      status: 'watchlist',
      investment_type: 'undecided',
      property_type: 'etw',
      num_units: 1
    },
  });

  // Helper für Number-Inputs: konvertiert leere Strings und NaN zu null
  const registerNumber = (name: keyof FormData) => register(name, {
    setValueAs: (v: string) => {
      if (v === '' || v === null || v === undefined) return null;
      const num = Number(v);
      return isNaN(num) ? null : num;
    }
  });

  const watchStatus = useWatch({ control, name: 'status' });
  const watchInvestmentType = useWatch({ control, name: 'investment_type' });
  const watchPurchasePrice = useWatch({ control, name: 'purchase_price' });
  const watchBundesland = useWatch({ control, name: 'bundesland' });

  // Sichtbarkeit von Sektionen
  const showCostsSection = watchStatus !== 'dismissed';
  const showIncomeSection = watchStatus !== 'dismissed' && watchInvestmentType !== 'self_used';
  const showHoldingCosts = ['purchased', 'in_renovation', 'for_sale'].includes(watchStatus);
  const showRunningCosts = ['in_portfolio', 'purchased', 'in_renovation', 'for_sale'].includes(watchStatus) && watchInvestmentType !== 'self_used';
  const showRenovation = watchInvestmentType === 'fix_and_flip' || ['in_renovation', 'purchased'].includes(watchStatus);

  useEffect(() => {
    if (property) {
      reset({
        name: property.name,
        address_street: property.address_street || '',
        address_zip: property.address_zip || '',
        address_city: property.address_city || '',
        bundesland: property.bundesland,
        property_type: property.property_type,
        living_area_sqm: property.living_area_sqm,
        land_area_sqm: property.land_area_sqm,
        year_built: property.year_built,
        num_units: property.num_units,
        floor: property.floor,
        num_floors: property.num_floors,
        status: property.status,
        investment_type: property.investment_type,
        dismissal_reason: property.dismissal_reason,
        dismissal_notes: property.dismissal_notes,
        deal_source: property.deal_source,
        source_contact_name: property.source_contact_name,
        source_contact_phone: property.source_contact_phone,
        source_contact_email: property.source_contact_email,
        purchase_price: centsToEuros(property.purchase_price),
        additional_costs: centsToEuros(property.additional_costs),
        cost_transfer_tax: centsToEuros(property.cost_transfer_tax),
        cost_notary: centsToEuros(property.cost_notary),
        cost_land_registry: centsToEuros(property.cost_land_registry),
        cost_broker: centsToEuros(property.cost_broker),
        cost_other: centsToEuros(property.cost_other),
        renovation_cost_total: centsToEuros(property.renovation_cost_total),
        holding_period_start: property.holding_period_start,
        holding_period_end: property.holding_period_end,
        holding_cost_monthly: centsToEuros(property.holding_cost_monthly),
        running_cost_hausgeld: centsToEuros(property.running_cost_hausgeld),
        running_cost_hausgeld_non_allocable: centsToEuros(property.running_cost_hausgeld_non_allocable),
        running_cost_reserve: centsToEuros(property.running_cost_reserve),
        running_cost_management: centsToEuros(property.running_cost_management),
        running_cost_insurance: centsToEuros(property.running_cost_insurance),
        running_cost_property_tax: centsToEuros(property.running_cost_property_tax),
        running_cost_other: centsToEuros(property.running_cost_other),
        rent_cold: centsToEuros(property.rent_cold),
        rent_warm: centsToEuros(property.rent_warm),
        current_value: centsToEuros(property.current_value),
        notes: property.notes,
      });
      setRenovationItems(property.renovation_items || []);
    } else {
      reset({
        name: '',
        status: 'watchlist',
        investment_type: 'undecided',
        property_type: 'etw',
        num_units: 1,
        address_street: '',
        address_zip: '',
        address_city: '',
      });
      setRenovationItems([]);
    }
    setActiveTab('basic');
  }, [property, reset, open]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        ...data,
        purchase_price: eurosToCents(data.purchase_price ?? null),
        additional_costs: eurosToCents(data.additional_costs ?? null),
        cost_transfer_tax: eurosToCents(data.cost_transfer_tax ?? null),
        cost_notary: eurosToCents(data.cost_notary ?? null),
        cost_land_registry: eurosToCents(data.cost_land_registry ?? null),
        cost_broker: eurosToCents(data.cost_broker ?? null),
        cost_other: eurosToCents(data.cost_other ?? null),
        renovation_cost_total: eurosToCents(data.renovation_cost_total ?? null),
        holding_cost_monthly: eurosToCents(data.holding_cost_monthly ?? null),
        running_cost_hausgeld: eurosToCents(data.running_cost_hausgeld ?? null),
        running_cost_hausgeld_non_allocable: eurosToCents(data.running_cost_hausgeld_non_allocable ?? null),
        running_cost_reserve: eurosToCents(data.running_cost_reserve ?? null),
        running_cost_management: eurosToCents(data.running_cost_management ?? null),
        running_cost_insurance: eurosToCents(data.running_cost_insurance ?? null),
        running_cost_property_tax: eurosToCents(data.running_cost_property_tax ?? null),
        running_cost_other: eurosToCents(data.running_cost_other ?? null),
        rent_cold: eurosToCents(data.rent_cold ?? null),
        rent_warm: eurosToCents(data.rent_warm ?? null),
        current_value: eurosToCents(data.current_value ?? null),
      };

      if (isEditing && property) {
        await updateProperty(property.id, payload as any);
        toast.success('Immobilie erfolgreich aktualisiert');
      } else {
        await createProperty(payload as any);
        toast.success('Immobilie erfolgreich erstellt');
      }
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten';
      toast.error(isEditing ? 'Fehler beim Aktualisieren' : 'Fehler beim Erstellen', {
        description: message
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClose={() => onOpenChange(false)} className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>{isEditing ? 'Immobilie bearbeiten' : 'Neue Immobilie'}</DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex px-6 border-b border-border">
          {FORM_TABS.map((tab) => {
            if (tab.id === 'costs' && !showCostsSection) return null;
            if (tab.id === 'income' && !showIncomeSection) return null;
            
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                  activeTab === tab.id
                    ? 'border-foreground text-foreground'
                    : 'border-transparent text-foreground-muted hover:text-foreground'
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6" noValidate>
          <div className="space-y-6">
            <AnimatePresence mode="wait">
              {/* TAB: Basisdaten */}
              {activeTab === 'basic' && (
                <motion.div
                  key="basic"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="space-y-4"
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input id="name" placeholder="z.B. Musterstraße 1" {...register('name')} error={errors.name?.message} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status *</Label>
                      <Select id="status" {...register('status')}>
                        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                          <option key={key} value={key}>{config.label}</option>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="investment_type">Investment-Typ</Label>
                      <Select id="investment_type" {...register('investment_type')}>
                        {Object.entries(INVESTMENT_TYPE_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="property_type">Objekttyp *</Label>
                      <Select id="property_type" {...register('property_type')}>
                        {Object.entries(PROPERTY_TYPE_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address_street">Straße</Label>
                    <Input id="address_street" placeholder="Hauptstraße 123" {...register('address_street')} />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="address_zip">PLZ</Label>
                      <Input id="address_zip" placeholder="12345" {...register('address_zip')} />
                    </div>
                    <div className="sm:col-span-2 space-y-2">
                      <Label htmlFor="address_city">Ort</Label>
                      <Input id="address_city" placeholder="Berlin" {...register('address_city')} />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="living_area_sqm">Wohnfläche (m²)</Label>
                      <Input id="living_area_sqm" type="number" step="0.01" {...registerNumber('living_area_sqm')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="land_area_sqm">Grundstücksfläche (m²)</Label>
                      <Input id="land_area_sqm" type="number" step="0.01" {...registerNumber('land_area_sqm')} />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="year_built">Baujahr</Label>
                      <Input id="year_built" type="number" {...registerNumber('year_built')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="num_units">Einheiten</Label>
                      <Input id="num_units" type="number" {...registerNumber('num_units')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="floor">Etage</Label>
                      <Input id="floor" type="number" {...registerNumber('floor')} />
                    </div>
                  </div>

                  {watchStatus === 'dismissed' && (
                    <div className="p-4 rounded-lg bg-negative/5 border border-negative/10 space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="dismissal_reason">Grund für Verwurf</Label>
                        <Select id="dismissal_reason" {...register('dismissal_reason')}>
                          <option value="">Grund auswählen...</option>
                          {Object.entries(DISMISSAL_REASON_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dismissal_notes">Anmerkungen zum Verwurf</Label>
                        <Textarea id="dismissal_notes" placeholder="Warum genau verworfen?" {...register('dismissal_notes')} />
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* TAB: Kosten */}
              {activeTab === 'costs' && showCostsSection && (
                <motion.div
                  key="costs"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <Label htmlFor="purchase_price">Kaufpreis (€)</Label>
                    <Input id="purchase_price" type="number" step="1000" {...registerNumber('purchase_price')} />
                  </div>

                  <Controller
                    name="additional_costs"
                    control={control}
                    render={({ field }) => (
                      <AdditionalCostsInput
                        purchasePrice={eurosToCents(watchPurchasePrice || 0)}
                        bundesland={watchBundesland as Bundesland}
                        values={{
                          total: eurosToCents(field.value || 0),
                          transferTax: eurosToCents(watch('cost_transfer_tax') || 0),
                          notary: eurosToCents(watch('cost_notary') || 0),
                          landRegistry: eurosToCents(watch('cost_land_registry') || 0),
                          broker: eurosToCents(watch('cost_broker') || 0),
                          other: eurosToCents(watch('cost_other') || 0),
                        }}
                        onChange={(values) => {
                          setValue('additional_costs', centsToEuros(values.total));
                          setValue('cost_transfer_tax', centsToEuros(values.transferTax));
                          setValue('cost_notary', centsToEuros(values.notary));
                          setValue('cost_land_registry', centsToEuros(values.landRegistry));
                          setValue('cost_broker', centsToEuros(values.broker));
                          setValue('cost_other', centsToEuros(values.other));
                        }}
                        onBundeslandChange={(bl) => setValue('bundesland', bl)}
                      />
                    )}
                  />

                  {showRenovation && (
                    <Controller
                      name="renovation_cost_total"
                      control={control}
                      render={({ field }) => (
                        <RenovationCostsInput
                          totalValue={eurosToCents(field.value || 0)}
                          onTotalChange={(v) => field.onChange(centsToEuros(v))}
                          items={renovationItems}
                          onItemsChange={setRenovationItems}
                        />
                      )}
                    />
                  )}

                  {showHoldingCosts && (
                    <HoldingCostsInput
                      startDate={watch('holding_period_start') || null}
                      endDate={watch('holding_period_end') || null}
                      monthlyTotal={eurosToCents(watch('holding_cost_monthly') || 0)}
                      details={{
                        financing: null,
                        utilities: null,
                        insurance: null,
                        other: null,
                      }}
                      onChange={(values) => {
                        setValue('holding_period_start', values.startDate);
                        setValue('holding_period_end', values.endDate);
                        setValue('holding_cost_monthly', centsToEuros(values.monthlyTotal));
                      }}
                    />
                  )}
                </motion.div>
              )}

              {/* TAB: Einnahmen */}
              {activeTab === 'income' && showIncomeSection && (
                <motion.div
                  key="income"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="space-y-6"
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="rent_cold">Kaltmiete / Monat (€)</Label>
                      <Input id="rent_cold" type="number" step="10" {...registerNumber('rent_cold')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rent_warm">Warmmiete / Monat (€)</Label>
                      <Input id="rent_warm" type="number" step="10" {...registerNumber('rent_warm')} />
                    </div>
                  </div>

                  {showRunningCosts && (
                    <RunningCostsInput
                      values={{
                        hausgeld: eurosToCents(watch('running_cost_hausgeld') || 0),
                        hausgeldNonAllocable: eurosToCents(watch('running_cost_hausgeld_non_allocable') || 0),
                        reserve: eurosToCents(watch('running_cost_reserve') || 0),
                        management: eurosToCents(watch('running_cost_management') || 0),
                        insurance: eurosToCents(watch('running_cost_insurance') || 0),
                        propertyTax: eurosToCents(watch('running_cost_property_tax') || 0),
                        other: eurosToCents(watch('running_cost_other') || 0),
                      }}
                      onChange={(values) => {
                        setValue('running_cost_hausgeld', centsToEuros(values.hausgeld));
                        setValue('running_cost_hausgeld_non_allocable', centsToEuros(values.hausgeldNonAllocable));
                        setValue('running_cost_reserve', centsToEuros(values.reserve));
                        setValue('running_cost_management', centsToEuros(values.management));
                        setValue('running_cost_insurance', centsToEuros(values.insurance));
                        setValue('running_cost_property_tax', centsToEuros(values.propertyTax));
                        setValue('running_cost_other', centsToEuros(values.other));
                      }}
                    />
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="current_value">Aktueller Marktwert (€)</Label>
                    <Input id="current_value" type="number" step="1000" {...registerNumber('current_value')} />
                  </div>
                </motion.div>
              )}

              {/* TAB: Quelle */}
              {activeTab === 'source' && (
                <motion.div
                  key="source"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="deal_source">Quelle</Label>
                    <Select id="deal_source" {...register('deal_source')}>
                      <option value="">Quelle auswählen...</option>
                      {Object.entries(DEAL_SOURCE_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="pt-4 border-t border-border">
                    <h4 className="text-sm font-medium mb-4">Kontaktperson</h4>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="source_contact_name">Name</Label>
                        <Input id="source_contact_name" placeholder="z.B. Max Mustermann" {...register('source_contact_name')} />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="source_contact_phone">Telefon</Label>
                          <Input id="source_contact_phone" placeholder="0123 / 456789" {...register('source_contact_phone')} />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="source_contact_email">E-Mail</Label>
                          <Input id="source_contact_email" type="email" placeholder="max@beispiel.de" {...register('source_contact_email')} />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* TAB: Notizen */}
              {activeTab === 'notes' && (
                <motion.div
                  key="notes"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="space-y-2"
                >
                  <Label htmlFor="notes">Interne Notizen</Label>
                  <Textarea id="notes" rows={8} placeholder="Warum ist dieses Objekt interessant? Besondere Merkmale?" {...register('notes')} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <DialogFooter className="mt-8">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" isLoading={isLoading}>{isEditing ? 'Speichern' : 'Erstellen'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
